/**
 * Binds an inspector controller to `?inspect=`, so the open panel is part of
 * the address rather than part of a component's memory.
 *
 * Composition rather than a flag inside {@link useInspector}: that hook is the
 * panel's state machine and has no router in it, its tests are pure, and every
 * link component in the app builds one. Only the four pages that actually MOUNT
 * a panel should be writing to the URL, so the router coupling lives here and is
 * applied to their controller alone.
 *
 * ## Push once, then replace
 *
 * Opening the panel PUSHES a history entry — that is the whole point, because it
 * is what makes Back close the panel instead of leaving the page. Moving the
 * panel to another subject while it is open REPLACES, which is the same rule
 * every tab strip in this app follows: stepping through six counterparties must
 * not bury the list behind six Back presses.
 *
 * Closing goes BACK when we are the ones who pushed the entry, so the stack ends
 * up exactly as it was before the panel opened. The alternative — replacing the
 * entry with a panel-less copy — leaves two identical entries, and the reader's
 * next Back appears to do nothing. When we did not push (a deep link straight
 * into an open panel, or an entry something else has since replaced) there is
 * nothing to go back TO, so the parameter is dropped in place instead.
 *
 * ## Whose write was that
 *
 * The parameter changes for two very different reasons — we wrote it, or the
 * reader pressed Back / Forward / pasted a link — and only the second should
 * move the panel. `written` holds the last value we put there; anything else
 * arriving is external, and is adopted.
 */
import * as React from "react";
import { useNavigate, useRouter, useSearch } from "@tanstack/react-router";
import { targetKey } from "./model";
import type { InspectorController } from "./useInspector";
import {
  INSPECT_ELEMENT,
  INSPECT_PARAM,
  inspectRef,
  subjectFromInspectRef,
} from "./inspectorParam";

/**
 * How long a dismiss waits before it touches history.
 *
 * Long enough to cover `pointerdown` → `click` on the element underneath (a few
 * milliseconds, more on a slow or deliberate click), short enough that the
 * address settles while the reader is still looking at the panel closing.
 */
const DISMISS_GRACE_MS = 250;

export interface UseInspectorUrlOptions {
  /**
   * Whether this controller owns the address bar.
   *
   * False for every controller with no panel beside it — they must not write a
   * parameter for a panel that will never draw. The hook still runs (hooks
   * cannot be conditional); it simply returns the controller untouched.
   */
  bind: boolean;
}

export function useInspectorUrl(
  inspector: InspectorController,
  { bind }: UseInspectorUrlOptions,
): InspectorController {
  const navigate = useNavigate();
  const router = useRouter();
  // `strict: false` because this hook is mounted from four different routes and
  // must not care which; an unmodelled param reads as undefined rather than
  // throwing.
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const param = typeof search[INSPECT_PARAM] === "string" ? (search[INSPECT_PARAM] as string) : null;

  const written = React.useRef<string | null>(null);
  /**
   * A write we have asked for and not yet seen.
   *
   * `navigate` does not change the address synchronously. Opening the panel also
   * sets React state, so a render happens FIRST, and in it `written` already
   * holds the new ref while `param` is still the old one. Without this flag the
   * effect below read that gap as "somebody removed the parameter" — on the very
   * navigation it had just requested — and cleared `pushed`, so the close that
   * followed no longer knew it had an entry to unwind. That was the whole of the
   * "Back does nothing after closing the panel" defect; it survived a unit test
   * that moved the URL in the same tick, and only showed up in the browser.
   */
  const pending = React.useRef(false);
  const pushed = React.useRef(false);
  /**
   * A close already in flight.
   *
   * Radix announces a dismiss more than once — the close button's own click and
   * the dismissable layer's pointer-down-outside both reach `onOpenChange(false)`
   * — and `router.history.back()` does not take effect until the next tick. So
   * the second call saw a URL that still had the parameter and an entry we no
   * longer owned, took the fall-back branch, and wrote a panel-less copy of the
   * page over the entry Back had just returned to. The reader's next Back then
   * landed on an identical URL and appeared to do nothing, which is the exact
   * papercut going back rather than replacing was meant to avoid. Measured: open
   * the panel on `/transactions`, close it, press Back — and stay on
   * `/transactions` instead of returning to `/addresses`.
   */
  const closing = React.useRef(false);
  /**
   * A dismiss whose history unwind has not fired yet.
   *
   * `DrilldownPanel` is not modal, so ONE outside click both dismisses the panel
   * and reaches whatever is underneath — which on a canvas is usually the next
   * node or edge the reader wants to inspect. The dismiss arrives on
   * `pointerdown` and the selection on `click`, in separate turns, so by the time
   * `router.history.back()` landed it was unwinding the entry the NEW target had
   * just written: the panel ended up showing node B while closed, with an empty
   * address. Measured in a case graph, clicking straight from one node to
   * another.
   *
   * So a dismiss closes the panel at once and lets the URL settle a moment
   * later, and an `open` arriving in that window cancels the unwind — because a
   * dismiss immediately followed by a re-open was never a close. The explicit
   * close button does not wait: nothing follows it.
   */
  const unwind = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelUnwind = React.useCallback(() => {
    if (unwind.current === null) return;
    clearTimeout(unwind.current);
    unwind.current = null;
  }, []);
  React.useEffect(() => cancelUnwind, [cancelUnwind]);

  const { open: openTarget, close: closeTarget, setOpen: setPanelOpen, target, isOpen } = inspector;

  /**
   * Adopt a parameter WE did not write: a deep link on first paint, a Back that
   * dropped it, a Forward that brought it back.
   */
  React.useEffect(() => {
    if (!bind) return;
    if (param === written.current) {
      // Our own write has landed.
      pending.current = false;
      return;
    }
    // Still waiting for a navigation we asked for: the difference is ours, not
    // the reader's. See `pending`.
    if (pending.current) return;
    written.current = param;
    // The parameter moved, so whatever close was in flight has landed.
    closing.current = false;
    if (!param) {
      // Back, off the entry that opened the panel. Close without touching the
      // URL — it is already right, and writing to it here would push a third
      // entry onto a stack the reader is in the middle of walking.
      pushed.current = false;
      setPanelOpen(false);
      return;
    }
    const subject = subjectFromInspectRef(param);
    if (!subject) {
      // Somebody else's `?inspect=element` — a link copied while a scene target
      // was open. It names nothing we can restore, so it is swept out of the
      // address rather than left there meaning nothing. Any OTHER unusable value
      // is left alone: it may be a newer spelling this build does not know, and
      // rewriting a stranger's URL on a guess is worse than ignoring it.
      if (param === INSPECT_ELEMENT) writeParamRef.current?.(null, "replace");
      return;
    }
    // Arrived already open, so there is no entry of ours beneath this one.
    pushed.current = false;
    if (target && targetKey(subject) === targetKey(target) && isOpen) return;
    openTarget(subject);
  }, [bind, param, openTarget, setPanelOpen, target, isOpen]);

  /**
   * A forward reference, because the adopt effect above has to be able to sweep
   * a stale `?inspect=element` and is declared before the writer.
   */
  const writeParamRef = React.useRef<((next: string | null, mode: "push" | "replace") => void) | null>(
    null,
  );

  const writeParam = React.useCallback(
    (next: string | null, mode: "push" | "replace") => {
      written.current = next;
      pending.current = true;
      // `Promise.resolve(...)`: a host (or a test stub) whose `navigate` returns
      // nothing must not throw here — and must still clear the flag.
      void Promise.resolve(
        navigate({
        // Everything else in the address is somebody else's — a filter, a tab, a
        // chain — and the panel has no business editing it.
        search: (prev: Record<string, unknown>) => ({ ...prev, [INSPECT_PARAM]: next ?? undefined }),
        hash: true,
        replace: mode === "replace",
          // A navigation that never commits must not leave the hook deaf to
          // Back for the rest of the page's life. The effect clears this too, as
          // soon as it sees the value arrive.
        } as never),
      ).finally(() => {
        pending.current = false;
      });
    },
    [navigate],
  );

  writeParamRef.current = writeParam;

  const open = React.useCallback<InspectorController["open"]>(
    (next) => {
      openTarget(next);
      if (!bind) return;
      cancelUnwind();
      closing.current = false;
      // A scene target (an edge, a node, a collection) cannot be named in a URL,
      // so it writes the sentinel: never the previous subject's ref, which would
      // describe something that is no longer on screen, and never nothing, which
      // would leave Back with no entry to consume. See `INSPECT_ELEMENT`.
      const ref = inspectRef(next) ?? INSPECT_ELEMENT;
      if (ref === param) return;
      // Already showing a panel means the reader is moving BETWEEN targets, and
      // each step must not become a Back press.
      const alreadyOpen = param !== null;
      writeParam(ref, alreadyOpen ? "replace" : "push");
      if (!alreadyOpen) pushed.current = true;
    },
    [bind, openTarget, param, writeParam, cancelUnwind],
  );

  /** The history half of a close, once we know no re-open is coming. */
  const settleClosedUrl = React.useCallback(() => {
    if (!bind || param === null) return;
    // See `closing`: one dismiss can arrive twice, and the second must not act
    // on a URL the first has already asked the router to change.
    if (closing.current) return;
    closing.current = true;
    if (pushed.current) {
      // Undo our own entry and let the URL drive: the effect above closes the
      // panel when the parameter disappears.
      //
      // `written` is deliberately LEFT holding the ref we are navigating away
      // from. Clearing it here — which this did at first — makes the very next
      // render read the parameter (still present, because `back()` lands a tick
      // later) as somebody else's change: the effect then re-adopted it, cleared
      // both flags, and the second half of the same dismiss took the branch
      // below and wrote a panel-less copy of the page over the entry we had just
      // returned to. Measured as "Back does nothing" on /transactions.
      pushed.current = false;
      router.history.back();
      return;
    }
    writeParam(null, "replace");
  }, [bind, param, router, writeParam]);

  /** The close button: nothing follows it, so the URL settles at once. */
  const close = React.useCallback(() => {
    cancelUnwind();
    closeTarget();
    settleClosedUrl();
  }, [cancelUnwind, closeTarget, settleClosedUrl]);

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (next) {
        cancelUnwind();
        setPanelOpen(true);
        return;
      }
      // A DISMISS — the outside click or Escape. The panel shuts immediately;
      // the address waits, because that same click may be the one selecting the
      // next target. See `unwind`.
      closeTarget();
      cancelUnwind();
      unwind.current = setTimeout(settleClosedUrl, DISMISS_GRACE_MS);
    },
    [cancelUnwind, closeTarget, setPanelOpen, settleClosedUrl],
  );

  return React.useMemo<InspectorController>(
    () => (bind ? { ...inspector, open, close, setOpen } : inspector),
    [bind, inspector, open, close, setOpen],
  );
}
